import axios, { AxiosError, Method } from 'axios';

import {
  REDUX_CLIENTCODE,
  REDUX_SERVICE_ENDPOINTS,
  REDUX_SESSIONKEY,
} from 'constants/persistence';
import { getServiceEndpoints } from 'services/ErplyAPI/getServiceEndpoints';
import { proxy } from 'services/shared';
import { ObfuscatedLocalStorage } from 'utils/ObfuscatedLocalStorage';

export interface RequestProps<D> {
  endpoint: string;
  method: Method;
  data?: D;
  params?: Record<string, unknown>;
}

function getFromStorage<T>(
  storage: typeof localStorage | typeof ObfuscatedLocalStorage,
  key,
  defaultValue: T,
) {
  try {
    return JSON.parse(storage.getItem(key)!);
  } catch (e) {
    return defaultValue;
  }
}

const getUrl = async (): Promise<string> => {
  const lsUrl = getFromStorage(localStorage, REDUX_SERVICE_ENDPOINTS, {}).auth;
  if (lsUrl?.url) {
    return lsUrl.url;
  }
  const [payload] = await getServiceEndpoints();
  return payload?.['auth']?.url;
};

const doRequest = async <D, R>({
  endpoint,
  method,
  data,
  params,
}: RequestProps<D>): Promise<{ data: R }> => {
  const clientCode = getFromStorage(
    ObfuscatedLocalStorage,
    REDUX_CLIENTCODE,
    '',
  );
  const sessionKey = getFromStorage(
    ObfuscatedLocalStorage,
    REDUX_SESSIONKEY,
    '',
  );

  const url = await getUrl();
  return axios({
    url: `${proxy}${url}${endpoint}`,
    method,
    data,
    params,
    headers: { clientCode, sessionKey },
  }).then(res => res.data);
};

function errorFromResponse(err: AxiosError) {
  throw new Error(err.response?.data?.status?.errorDescription ?? err.message);
}

export function initAdminSession() {
  return doRequest<
    never,
    { channel: 'PASSWORD' | 'EMAIL' | 'GOOGLE_APP' | 'MS_APP'; token: string }
  >({
    method: 'GET',
    endpoint: 'v1/session/admin',
  }).then(res => res.data, errorFromResponse);
}
export function confirmAdminSession(data: { key: string; token: string }) {
  return doRequest<{ key: string; token: string }, null>({
    method: 'POST',
    endpoint: 'v1/session/admin',
    data,
  }).catch(errorFromResponse);
}
export function deleteAdminSession() {
  return doRequest<never, string>({
    method: 'DELETE',
    endpoint: 'v1/session/admin',
  }).catch(errorFromResponse);
}
